import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import MetricCard from './components/MetricCard';
import TransactionFlowVisualization from './components/TransactionFlowVisualization';
import EventFeed from './components/EventFeed';
import PerformanceChart from './components/PerformanceChart';
import QuickActions from './components/QuickActions';

const RealTimeOperations = () => {
  const [metrics, setMetrics] = useState({
    transactionSuccess: { value: 99.7, trend: 'up', trendValue: '+0.3%' },
    swapPerformance: { value: 98.4, trend: 'up', trendValue: '+1.2%' },
    walletConnectivity: { value: 97.8, trend: 'down', trendValue: '-0.5%' },
    routingEfficiency: { value: 94.2, trend: 'up', trendValue: '+2.1%' },
    fraudAlerts: { value: 3, trend: 'down', trendValue: '-2' },
    systemUptime: { value: 99.99, trend: 'stable', trendValue: '0%' }
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setMetrics(prev => ({
        transactionSuccess: {
          value: Math.min(100, Math.max(95, prev?.transactionSuccess?.value + (Math.random() - 0.5) * 0.5)),
          trend: Math.random() > 0.5 ? 'up' : 'down',
          trendValue: `${Math.random() > 0.5 ? '+' : '-'}${(Math.random() * 0.5)?.toFixed(1)}%`
        },
        swapPerformance: {
          value: Math.min(100, Math.max(95, prev?.swapPerformance?.value + (Math.random() - 0.5) * 0.8)),
          trend: Math.random() > 0.5 ? 'up' : 'down',
          trendValue: `${Math.random() > 0.5 ? '+' : '-'}${(Math.random() * 1.5)?.toFixed(1)}%`
        },
        walletConnectivity: {
          value: Math.min(100, Math.max(90, prev?.walletConnectivity?.value + (Math.random() - 0.5) * 1)),
          trend: Math.random() > 0.5 ? 'up' : 'down',
          trendValue: `${Math.random() > 0.5 ? '+' : '-'}${(Math.random() * 1)?.toFixed(1)}%`
        },
        routingEfficiency: {
          value: Math.min(100, Math.max(85, prev?.routingEfficiency?.value + (Math.random() - 0.5) * 1.5)),
          trend: Math.random() > 0.5 ? 'up' : 'down',
          trendValue: `${Math.random() > 0.5 ? '+' : '-'}${(Math.random() * 2)?.toFixed(1)}%`
        },
        fraudAlerts: {
          value: Math.max(0, Math.floor(prev?.fraudAlerts?.value + (Math.random() - 0.7) * 2)),
          trend: Math.random() > 0.5 ? 'up' : 'down',
          trendValue: `${Math.random() > 0.5 ? '+' : '-'}${Math.floor(Math.random() * 3)}`
        },
        systemUptime: {
          value: Math.min(100, Math.max(99.9, prev?.systemUptime?.value + (Math.random() - 0.5) * 0.01)),
          trend: 'stable',
          trendValue: '0%'
        }
      }));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getMetricStatus = (value, type) => {
    if (type === 'fraud') {
      return value <= 5 ? 'success' : value <= 10 ? 'warning' : 'error';
    }
    return value >= 98 ? 'success' : value >= 95 ? 'warning' : 'error';
  };

  return (
    <MainLayout showControls={true}>
      <div className="max-w-[1400px] mx-auto space-y-6 md:space-y-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">
            Real-Time Operations Dashboard
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            Live monitoring of transaction flows, system health, and operational metrics across all payment rails
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 md:gap-6">
          <MetricCard
            title="Transaction Success"
            value={metrics?.transactionSuccess?.value?.toFixed(1)}
            unit="%"
            trend={metrics?.transactionSuccess?.trend}
            trendValue={metrics?.transactionSuccess?.trendValue}
            status={getMetricStatus(metrics?.transactionSuccess?.value, 'success')}
            icon="CheckCircle"
            lastUpdate="2s ago"
            onClick={() => {}}
          />
          <MetricCard
            title="Swap Performance"
            value={metrics?.swapPerformance?.value?.toFixed(1)}
            unit="%"
            trend={metrics?.swapPerformance?.trend}
            trendValue={metrics?.swapPerformance?.trendValue}
            status={getMetricStatus(metrics?.swapPerformance?.value, 'success')}
            icon="RefreshCw"
            lastUpdate="3s ago"
            onClick={() => {}}
          />
          <MetricCard
            title="Wallet Connectivity"
            value={metrics?.walletConnectivity?.value?.toFixed(1)}
            unit="%"
            trend={metrics?.walletConnectivity?.trend}
            trendValue={metrics?.walletConnectivity?.trendValue}
            status={getMetricStatus(metrics?.walletConnectivity?.value, 'success')}
            icon="Wallet"
            lastUpdate="5s ago"
            onClick={() => {}}
          />
          <MetricCard
            title="Routing Efficiency"
            value={metrics?.routingEfficiency?.value?.toFixed(1)}
            unit="%"
            trend={metrics?.routingEfficiency?.trend}
            trendValue={metrics?.routingEfficiency?.trendValue}
            status={getMetricStatus(metrics?.routingEfficiency?.value, 'success')}
            icon="Route"
            lastUpdate="4s ago"
            onClick={() => {}}
          />
          <MetricCard
            title="Fraud Alerts"
            value={metrics?.fraudAlerts?.value}
            unit="active"
            trend={metrics?.fraudAlerts?.trend}
            trendValue={metrics?.fraudAlerts?.trendValue}
            status={getMetricStatus(metrics?.fraudAlerts?.value, 'fraud')}
            icon="Shield"
            lastUpdate="1s ago"
            onClick={() => {}}
          />
          <MetricCard
            title="System Uptime"
            value={metrics?.systemUptime?.value?.toFixed(2)}
            unit="%"
            trend={metrics?.systemUptime?.trend}
            trendValue={metrics?.systemUptime?.trendValue}
            status="success"
            icon="Activity"
            lastUpdate="Just now"
            onClick={() => {}}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          <div className="lg:col-span-8">
            <TransactionFlowVisualization />
          </div>
          <div className="lg:col-span-4">
            <EventFeed />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:gap-8">
          <PerformanceChart />
        </div>

        <div className="grid grid-cols-1 gap-6 md:gap-8">
          <QuickActions />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <div className="bg-card rounded-xl border border-border p-4 md:p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground">Active Transactions</h3>
              <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-foreground font-data mb-2">1,247</p>
            <p className="text-xs text-muted-foreground caption">Processing across all rails</p>
          </div>

          <div className="bg-card rounded-xl border border-border p-4 md:p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground">Avg Response Time</h3>
              <div className="w-2 h-2 rounded-full bg-warning animate-pulse" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-foreground font-data mb-2">287ms</p>
            <p className="text-xs text-muted-foreground caption">Across all API endpoints</p>
          </div>

          <div className="bg-card rounded-xl border border-border p-4 md:p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground">Connected Wallets</h3>
              <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-foreground font-data mb-2">8,432</p>
            <p className="text-xs text-muted-foreground caption">Active wallet connections</p>
          </div>

          <div className="bg-card rounded-xl border border-border p-4 md:p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground">Error Rate</h3>
              <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-foreground font-data mb-2">0.31%</p>
            <p className="text-xs text-muted-foreground caption">Below 0.5% threshold</p>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default RealTimeOperations;